import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/AdminLayout";
import PayslipView from "@/components/PayslipView";
import { useCompanyFilter } from "@/lib/companyFilter";
import { isPaymentEligibleEmployee, isPaymentOnlyPayslip, resolvePayrollFlags } from "@/lib/payrollFlags";
import {
  Wallet,
  Play,
  Plus,
  Loader2,
  Eye,
  Trash2,
  RefreshCw,
  FileSpreadsheet,
  FileText,
  Download,
  RotateCw,
  Pencil,
  X,
  Banknote,
  Lock,
} from "lucide-react";

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const PAYROLL_ELIGIBLE_STATUSES = ["Active", "Onboarding", "Probation", "Notice Period"];

function statusBadgeClass(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-800";
  if (status === "Draft") return "bg-slate-100 text-slate-700";
  return "bg-blue-100 text-blue-800";
}

export default function Payroll() {
  const { user } = useAuth();
  const { companyId, companyOptions, setCompanyId, empParams } = useCompanyFilter();
  const canDelete = user?.role === "admin";
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ arrears: 0, bonus: 0, advance: 0, tds_rate_override: 0 });
  const [editErr, setEditErr] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  // Single-employee payroll generation
  const [singleOpen, setSingleOpen] = useState(false);
  const [singleForm, setSingleForm] = useState({ employee_id: "", arrears: 0, bonus: 0, advance: 0, tds_rate_override: 0 });
  const [singleErr, setSingleErr] = useState("");
  const [singleBusy, setSingleBusy] = useState(false);
  const [periodLocked, setPeriodLocked] = useState(false);

  const loadPeriodLock = async () => {
    try {
      const r = await api.get("/payroll-runs/period-lock", { params: { month, year } });
      setPeriodLocked(Boolean(r.data?.locked));
    } catch {
      setPeriodLocked(false);
    }
  };

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/payslips", { params: { month, year, ...empParams } }),
      api.get("/employees", { params: empParams }),
    ]);
    setPayslips(a.data);
    setEmployees(b.data);
    await loadPeriodLock();
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [month, year, companyId]);

  // ── Sidebar-driven action (?action=generate) ──
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const action = searchParams.get("action");
    if (!action) return;
    if (periodLocked && (action === "generate" || action === "bulk")) {
      setMsg("This month is locked. Unlock from Payroll Wizard to edit payslips.");
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
      return;
    }
    if (action === "generate") {
      setSingleForm({ employee_id: "", arrears: 0, bonus: 0, advance: 0, tds_rate_override: 0 });
      setSingleErr("");
      setSingleOpen(true);
    } else if (action === "bulk") {
      // Bulk generate for the currently selected month/year
      if (window.confirm(`Generate payslips for ${MONTHS[month - 1]} ${year} for all eligible employees?`)) {
        genBulk();
      }
    }
    const next = new URLSearchParams(searchParams);
    next.delete("action");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, periodLocked]);

  const empMap = useMemo(
    () => Object.fromEntries(employees.map((e) => [e.id, e])),
    [employees]
  );

  const genBulk = async () => {
    if (periodLocked) {
      setMsg("This month is locked. Unlock from Payroll Wizard to edit payslips.");
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const r = await api.post(`/payslips/generate-bulk`, null, {
        params: { month, year },
      });
      const payOnly = r.data.payment_only_generated || 0;
      const removed = r.data.removed || 0;
      const parts = [
        `Generated ${r.data.generated} payroll row(s)`,
        `Recomputed ${r.data.recomputed || 0} existing`,
        `Skipped ${r.data.skipped}`,
      ];
      if (r.data.skipped_existing) parts.push(`(${r.data.skipped_existing} already exist)`);
      if (payOnly > 0) parts.push(`${payOnly} payment-only`);
      if (removed > 0) parts.push(`${removed} removed (Include in payment=No)`);
      setMsg(parts.join(" · "));
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id) => {
    if (periodLocked) return;
    if (!confirm("Delete this payslip?")) return;
    try {
      await api.delete(`/payslips/${id}`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Delete failed");
    }
  };

  const regenerate = async (id) => {
    if (periodLocked) return;
    if (!confirm("Regenerate this payslip from latest employee data?")) return;
    try {
      await api.post(`/payslips/${id}/regenerate`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Regenerate failed");
    }
  };

  const markPaid = async (id) => {
    try {
      await api.patch(`/payslips/${id}/status`, { status: "Paid" });
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to mark Paid");
    }
  };

  const markAllPaid = async () => {
    if (!confirm(`Mark all payslips for ${MONTHS[month - 1]} ${year} as Paid?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/payslips/mark-paid", null, { params: { month, year } });
      setMsg(`Marked ${r.data.updated ?? r.data.matched ?? 0} payslip(s) as Paid.`);
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Failed to mark payslips Paid");
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (p) => {
    if (periodLocked) return;
    setEditing(p);
    setEditForm({
      arrears: p.arrears || 0,
      bonus: p.bonus || 0,
      advance: p.advance || 0,
      tds_rate_override: p.tds_rate || 0,
    });
    setEditErr("");
  };

  const activeEmployees = useMemo(
    () => employees.filter((emp) => isPaymentEligibleEmployee(emp, PAYROLL_ELIGIBLE_STATUSES)),
    [employees]
  );

  const submitOverrides = async (e) => {
    e.preventDefault();
    setEditBusy(true);
    setEditErr("");
    try {
      await api.put(`/payslips/${editing.id}/overrides`, editForm);
      setEditing(null);
      load();
    } catch (ex) {
      setEditErr(ex?.response?.data?.detail || "Failed to update");
    } finally {
      setEditBusy(false);
    }
  };

  const submitSingle = async (e) => {
    e.preventDefault();
    if (periodLocked) {
      setSingleErr("This month is locked. Unlock from Payroll Wizard to edit payslips.");
      return;
    }
    if (!singleForm.employee_id) {
      setSingleErr("Please select an employee");
      return;
    }
    setSingleBusy(true);
    setSingleErr("");
    try {
      await api.post("/payslips/generate", {
        employee_id: singleForm.employee_id,
        month,
        year,
        arrears: singleForm.arrears || 0,
        bonus: singleForm.bonus || 0,
        advance: singleForm.advance || 0,
        tds_rate_override: singleForm.tds_rate_override || null,
      });
      setSingleOpen(false);
      setSingleForm({ employee_id: "", arrears: 0, bonus: 0, advance: 0, tds_rate_override: 0 });
      setMsg(`Generated single payslip for ${MONTHS[month - 1]} ${year}`);
      load();
    } catch (ex) {
      setSingleErr(ex?.response?.data?.detail || "Failed to generate");
    } finally {
      setSingleBusy(false);
    }
  };

  const downloadFile = async (path, name) => {
    const res = await api.get(path, { responseType: "blob" });
    const blob = new Blob([res.data]);
    const link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const totalNet = payslips.reduce((sum, p) => sum + (p.net_pay || 0), 0);
  const totalGross = payslips.reduce((sum, p) => sum + (p.gross_earnings || 0), 0);
  const totalDed = payslips.reduce((sum, p) => sum + (p.total_deductions || 0), 0);

  return (
    <AdminLayout title="Payroll" breadcrumbs={["Home", "HR", "Payroll"]}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Payslips" value={payslips.length} color="#226DB4" icon={<Wallet className="h-5 w-5" />} />
        <StatCard label="Gross Total" value={`₹${totalGross.toLocaleString("en-IN")}`} color="#089949" />
        <StatCard label="Deductions" value={`₹${totalDed.toLocaleString("en-IN")}`} color="#dd4b39" />
        <StatCard label="Net Payout" value={`₹${totalNet.toLocaleString("en-IN")}`} color="#f39c12" />
      </div>

      <div className="bg-white border border-slate-200 rounded shadow-sm">
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <h3 className="text-base font-semibold text-slate-800">Payslips</h3>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white min-w-[160px]"
              data-testid="payroll-company-select"
            >
              {companyOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white"
              data-testid="payroll-month-select"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i + 1}>
                  {m}
                </option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}
              className="px-3 py-2 text-sm border border-slate-200 rounded bg-white"
              data-testid="payroll-year-select"
            >
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <button
              onClick={load}
              className="p-2 border border-slate-200 rounded hover:bg-slate-50 text-slate-600"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              data-testid="export-register-xlsx-btn"
              onClick={() => downloadFile(`/exports/salary-register.xlsx?month=${month}&year=${year}`, `salary_register_${year}_${String(month).padStart(2,"0")}.xlsx`)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
            >
              <FileSpreadsheet className="h-4 w-4 text-emerald-600" /> Salary Register (Excel)
            </button>
            <button
              data-testid="export-register-pdf-btn"
              onClick={() => downloadFile(`/exports/salary-register.pdf?month=${month}&year=${year}`, `salary_register_${year}_${String(month).padStart(2,"0")}.pdf`)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 text-slate-700"
            >
              <FileText className="h-4 w-4 text-rose-600" /> PDF
            </button>
            <button
              data-testid="mark-all-paid-btn"
              onClick={markAllPaid}
              disabled={busy || payslips.length === 0}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-emerald-200 bg-emerald-50 text-emerald-800 rounded hover:bg-emerald-100 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Banknote className="h-4 w-4" />}
              Mark all Paid
            </button>
            <button
              data-testid="generate-single-btn"
              onClick={() => {
                if (periodLocked) {
                  setMsg("This month is locked. Unlock from Payroll Wizard to edit payslips.");
                  return;
                }
                setSingleForm({ employee_id: "", arrears: 0, bonus: 0, advance: 0, tds_rate_override: 0 });
                setSingleErr("");
                setSingleOpen(true);
              }}
              disabled={periodLocked}
              className="hidden"
            >
              <Plus className="h-4 w-4" /> Generate Single
            </button>
            <button
              data-testid="generate-bulk-btn"
              onClick={genBulk}
              disabled={busy || periodLocked}
              className="hidden"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Generate Payslips for {MONTHS[month - 1]} {year}
            </button>
          </div>
        </div>
        {periodLocked && (
          <div className="px-5 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-900 flex items-center gap-2 flex-wrap" data-testid="payroll-locked-banner">
            <Lock className="h-4 w-4 shrink-0" />
            <span>This month is locked.</span>
            <a href="/payroll/wizard" className="underline font-medium text-amber-950 hover:text-amber-800">
              Open Generate Payroll → Unlock
            </a>
          </div>
        )}
        {msg && (
          <div className="px-5 py-2 bg-blue-50 border-b border-blue-100 text-sm text-blue-800" data-testid="payroll-msg">
            {msg}
          </div>
        )}

        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Code</th>
              <th className="text-left px-4 py-3 font-semibold">Employee</th>
              <th className="text-left px-4 py-3 font-semibold">Period</th>
              <th className="text-left px-4 py-3 font-semibold">Paid Days</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              <th className="text-right px-4 py-3 font-semibold">Gross</th>
              <th className="text-right px-4 py-3 font-semibold">Deductions</th>
              <th className="text-right px-4 py-3 font-semibold">Net Pay</th>
              <th className="text-right px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {payslips.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center text-slate-400" data-testid="payroll-empty">
                  No payslips for {MONTHS[month - 1]} {year}. Click &ldquo;Generate Payslips&rdquo; to create them.
                </td>
              </tr>
            ) : (
              payslips.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-medium text-slate-700">{p.employee_code}</td>
                  <td className="px-4 py-3 text-slate-800">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span>{p.employee_name}</span>
                      {isPaymentOnlyPayslip(p) && (
                        <span
                          className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#E8F0FF] text-[#226DB4]"
                          title="In payroll payment; no employee salary slip (ESS/PDF)"
                        >
                          Payment only
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{p.period_label}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {p.paid_days} / {p.working_days}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusBadgeClass(p.status || "Finalized")}`}>
                      {p.status || "Finalized"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">
                    ₹{p.gross_earnings.toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3 text-right text-rose-600">
                    ₹{p.total_deductions.toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-emerald-700">
                    ₹{p.net_pay.toLocaleString("en-IN")}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5">
                      {!isPaymentOnlyPayslip(p) && (
                        <>
                          <button
                            onClick={() => setViewing(p)}
                            className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
                            data-testid={`view-payslip-${p.employee_code}`}
                            title="View payslip"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => downloadFile(`/exports/payslip/${p.id}.pdf`, `payslip_${p.employee_code}_${p.year}_${String(p.month).padStart(2,"0")}.pdf`)}
                            className="p-1.5 text-[#226DB4] hover:bg-[#226DB4]/10 rounded"
                            data-testid={`download-payslip-${p.employee_code}`}
                            title="Download payslip PDF"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        </>
                      )}
                      {isPaymentOnlyPayslip(p) && (
                        <span
                          className="text-[10px] text-slate-400 mr-1"
                          title="Included in salary register / bank — no formal payslip"
                        >
                          No slip
                        </span>
                      )}
                      {p.status !== "Paid" && (
                        <button
                          onClick={() => markPaid(p.id)}
                          className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                          data-testid={`mark-paid-${p.employee_code}`}
                          title="Mark Paid"
                        >
                          <Banknote className="h-4 w-4" />
                        </button>
                      )}
                      {!periodLocked && (
                        <>
                          <button
                            onClick={() => openEdit(p)}
                            className="p-1.5 text-[#226DB4] hover:bg-[#226DB4]/10 rounded"
                            data-testid={`edit-payslip-${p.employee_code}`}
                            title="Edit (Arrears / Bonus / Advance / TDS Rate)"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => regenerate(p.id)}
                            className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                            data-testid={`regen-payslip-${p.employee_code}`}
                            title="Recalculate amounts"
                          >
                            <RotateCw className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => onDelete(p.id)}
                            className={`p-1.5 text-rose-500 hover:bg-rose-50 rounded ${canDelete ? "" : "hidden"}`}
                            data-testid={`delete-payslip-${p.employee_code}`}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {viewing && (
        <PayslipView
          payslip={viewing}
          employee={empMap[viewing.employee_id]}
          onClose={() => setViewing(null)}
        />
      )}

      {singleOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submitSingle} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Generate Single</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Period: {MONTHS[month - 1]} {year}. Anyone with Include in payment=Yes (Intern stipend included).
                </p>
              </div>
              <button type="button" onClick={() => setSingleOpen(false)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Employee</label>
              <select
                value={singleForm.employee_id}
                onChange={(e) => setSingleForm({ ...singleForm, employee_id: e.target.value })}
                required
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm bg-white"
                data-testid="single-employee-select"
              >
                <option value="">— Select employee —</option>
                {activeEmployees.map((emp) => {
                  const flags = resolvePayrollFlags(emp);
                  const payOnly = flags.include_in_payment && !flags.generate_payslip;
                  const name = [emp.basic?.first_name, emp.basic?.middle_name, emp.basic?.last_name]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <option key={emp.id} value={emp.id}>
                      {emp.basic?.code} · {name}
                      {payOnly ? " (payment only)" : ""}
                    </option>
                  );
                })}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Arrears (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={singleForm.arrears}
                  onChange={(e) => setSingleForm({ ...singleForm, arrears: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="single-arrears"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Bonus (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={singleForm.bonus}
                  onChange={(e) => setSingleForm({ ...singleForm, bonus: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="single-bonus"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Advance (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={singleForm.advance}
                  onChange={(e) => setSingleForm({ ...singleForm, advance: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="single-advance"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">TDS Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={singleForm.tds_rate_override}
                  onChange={(e) => setSingleForm({ ...singleForm, tds_rate_override: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="single-tds"
                />
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600">
              LOP days will be auto-calculated from approved unpaid leaves + Absent / Half-day attendance for{" "}
              {MONTHS[month - 1]} {year}. You can adjust overrides anytime after generation.
            </div>
            {singleErr && <div className="text-sm text-rose-600">{singleErr}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setSingleOpen(false)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button
                type="submit"
                disabled={singleBusy}
                className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-70"
                data-testid="single-submit"
              >
                {singleBusy ? "Generating…" : "Generate Payslip"}
              </button>
            </div>
          </form>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={submitOverrides} className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-slate-800">Adjust Payslip Components</h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  {editing.employee_name} · {editing.period_label}
                </p>
              </div>
              <button type="button" onClick={() => setEditing(null)} className="p-1.5 text-slate-400 hover:bg-slate-100 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Salary Arrears (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.arrears}
                  onChange={(e) => setEditForm({ ...editForm, arrears: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="override-arrears"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Bonus (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.bonus}
                  onChange={(e) => setEditForm({ ...editForm, bonus: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="override-bonus"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">Advance Deduction (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.advance}
                  onChange={(e) => setEditForm({ ...editForm, advance: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="override-advance"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">TDS Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.tds_rate_override}
                  onChange={(e) => setEditForm({ ...editForm, tds_rate_override: parseFloat(e.target.value) || 0 })}
                  className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                  data-testid="override-tds-rate"
                />
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600">
              These overrides apply only to this payslip. The system will recalculate gross, deductions
              and net pay using your values. TDS requires the employee&apos;s compliance flag to be enabled.
            </div>
            {editErr && <div className="text-sm text-rose-600">{editErr}</div>}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm border border-slate-200 rounded">
                Cancel
              </button>
              <button
                type="submit"
                disabled={editBusy}
                className="px-4 py-2 text-sm bg-[#226DB4] hover:bg-[#1B5A94] text-white rounded disabled:opacity-70"
                data-testid="override-submit"
              >
                {editBusy ? "Saving…" : "Apply & Recalculate"}
              </button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function StatCard({ label, value, color, icon }) {
  return (
    <div className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden flex items-stretch">
      <div
        className="flex items-center justify-center text-white px-5"
        style={{ backgroundColor: color }}
      >
        {icon || <span className="text-lg font-bold">₹</span>}
      </div>
      <div className="px-4 py-3 flex-1">
        <div className="text-xl font-bold text-slate-800 leading-tight">{value}</div>
        <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider font-medium">
          {label}
        </div>
      </div>
    </div>
  );
}
